

import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import _ from 'lodash';
import { Product } from '@/types';
import { urlFor } from '@/utils/sanityUtil';
import { sendToWhatsAPP } from '@/utils/whatsappUtil';

interface ProductItemProps {
  product: Product;
  variant?: 'default' | 'featured' | 'compact';
  showQuickBuy?: boolean;
}

const ProductItem = ({ product, variant = 'default', showQuickBuy = true }: ProductItemProps) => {
  const router = useRouter();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const price = useMemo(() => {
    const variantCount = product.variants?.length;
    if (variantCount === 1) {
      const productPrice = product.variants[0].price;
      return productPrice ? `LKR ${productPrice.toLocaleString()}` : 'Price on request';
    } else if (variantCount > 1) {
      const minPrice = _.minBy(product.variants, 'price')?.price;
      const maxPrice = _.maxBy(product.variants, 'price')?.price;
      return `LKR ${minPrice?.toLocaleString()} - ${maxPrice?.toLocaleString()}`;
    } else {
      return 'Price on request';
    }
  }, [product.variants]);

  const productUrl = `/shop/${product.productCategory?.slug?.current}/${product.slug.current}`;

  const handleQuickBuy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (product.variants?.length > 1) {
      router.push(productUrl);
    } else if (product.variants?.[0]) {
      const fullUrl = `https://chethis.lk${productUrl}`;
      const priceValue = product.variants[0].price;
      sendToWhatsAPP(router, product.title, 1, product.variants[0].sku, priceValue, fullUrl);
    }
  };

  const imageUrl = typeof product.mainImage === 'string' 
    ? product.mainImage 
    : urlFor(product.mainImage);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      whileHover={{ y: -4 }}
      className={`product-item product-item--${variant}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link href={productUrl} className="product-item__link">
        <div className="product-item__image-container">
          <div className="product-item__image-wrapper">
            <Image
              src={imageUrl}
              alt={product.title}
              fill
              sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
              className={`product-item__image ${imageLoaded ? 'product-item__image--loaded' : ''}`}
              onLoad={() => setImageLoaded(true)}
            />
            {!imageLoaded && (
              <div className="product-item__image-placeholder" />
            )}
          </div>
          
          {showQuickBuy && (
            <motion.button
              className="product-item__quick-buy"
              onClick={handleQuickBuy}
              initial={{ opacity: 0, y: 10 }}
              animate={{ 
                opacity: isHovered ? 1 : 0, 
                y: isHovered ? 0 : 10 
              }}
              transition={{ duration: 0.2 }}
            >
              {product.variants?.length > 1 ? 'View Options' : 'Quick Order'}
            </motion.button>
          )}
        </div>

        <div className="product-item__content">
          <h3 className="product-item__title">{product.title}</h3>
          <p className="product-item__price">{price}</p>
          {product.productCategory && (
            <p className="product-item__category">
              {product.productCategory.title}
            </p>
          )}
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductItem;
